import { Controller } from 'stimulus';

// The Tasks tab's Duration and Dates window (tasks/popup/_dates_form). One
// rule: the end is the start plus the Duration (Tasks::DurationDates, which
// counts the same way on save). Type a Duration and the end moves; pick an end
// and the Duration changes to match. Under a day it ends the day it starts;
// from a day its days count from the start, the people's working days for a
// whole-time task (data-dates-picker-working), calendar days otherwise.
//
// The Start and End fields over the calendar say which date the next click
// sets; picking the start moves on to End. Not started: the No Dates / Dates
// toggle, and the past can't be picked. Started: no toggle, its dates can be
// changed (the start not after today), and the Schedule never moves either
// date; the day it went In Process (data-dates-picker-since) is shown, not set.
// Finished (data-dates-picker-finished): the days it ran, past days only.
// Days the people don't work are hatched, their time off says Off. The
// window posts duration, dates_mode and start_date; the end follows on save.
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY = 1440;
const LIMIT = 366; // days a count looks ahead, as the server's horizon

export default class extends Controller {
  static targets = ['duration', 'was', 'mode', 'panel', 'start', 'startField', 'endField', 'prompt', 'calendar', 'note', 'usual']

  connect() {
    this.today = this.parse(this.data.get('today'));
    this.works = (this.data.get('works') || '').split(',').filter((d) => d !== '').map(Number);
    try { this.off = JSON.parse(this.data.get('off') || '{}'); } catch (_) { this.off = {}; }
    this.who = this.data.get('who') || '';
    this.count = Number(this.data.get('count') || 0);
    this.working = this.data.get('working') === 'true';
    this.started = this.data.get('started') === 'true';
    this.finished = this.data.get('finished') === 'true';
    this.since = this.data.get('since') || '';
    this.minutes = this.number(this.data.get('minutes'));
    this.stored = this.number(this.data.get('stored'));
    this.reconciled = this.number(this.data.get('reconciled'));
    this.startDay = this.hasStartTarget ? this.parse(this.startTarget.value) : null;
    this.active = this.started || this.finished || (this.startDay && this.startDay >= this.today) ? 'end' : 'start';
    const end = this.endFor(this.startDay, this.minutes);
    let from = this.startDay && this.startDay >= this.today ? this.startDay : this.nextWorkingDay();
    if (this.started) from = end && end >= this.today ? end : this.today;
    if (this.finished) from = end || this.startDay || this.today;
    this.month = new Date(from.getFullYear(), from.getMonth(), 1);
    if (this.hasCalendarTarget) {
      this.boundClick = this.click.bind(this);
      this.calendarTarget.addEventListener('click', this.boundClick);
    }
    this.draw();
    // (the popup editor placed it before the calendar was drawn)
    this.element.dispatchEvent(new CustomEvent('popup:resized', { bubbles: true }));
  }

  disconnect() {
    if (this.boundClick) this.calendarTarget.removeEventListener('click', this.boundClick);
  }

  // ---- actions ----

  pick(event) {
    const button = event.currentTarget;
    this.modeTarget.value = button.dataset.value;
    button.parentElement.querySelectorAll('button').forEach((b) => {
      b.classList.toggle('is-on', b === button);
      b.setAttribute('aria-pressed', String(b === button));
    });
    this.panelTargets.forEach((panel) => { panel.hidden = panel.dataset.panel !== button.dataset.value; });
  }

  durationChanged() {
    const value = this.readMinutes(this.durationTarget.value);
    if (Number.isNaN(value)) return; // still typing
    this.minutes = value;
    this.reconciled = null;
    if (this.hasUsualTarget) this.usualTarget.hidden = this.minutes === Number(this.usualTarget.dataset.minutes);
    this.draw();
  }

  // "Usually 3 hours · Use It": into the Duration, which moves the end
  useUsual(event) {
    event.preventDefault();
    this.durationTarget.value = event.currentTarget.dataset.value;
    this.durationChanged();
    this.durationTarget.focus();
  }

  // The Start or End field: what the next click on the calendar sets
  choose(event) {
    this.active = event.currentTarget.dataset.field;
    this.draw();
  }

  click(event) {
    const nav = event.target.closest('[data-month]');
    if (nav) {
      if (nav.disabled) return;
      this.month = new Date(this.month.getFullYear(), this.month.getMonth() + Number(nav.dataset.month), 1);
      this.draw();
      return;
    }
    const cell = event.target.closest('[data-day]');
    if (!cell || cell.disabled) return;
    const day = this.parse(cell.dataset.day);
    if (this.finished && day > this.today) return;
    if (this.active === 'start' || !this.startDay || day < this.startDay) {
      if (this.started && day > this.today) return;
      this.startDay = day;
      this.startTarget.value = this.key(day);
      this.active = 'end'; // its Duration comes along
    } else if (!(this.same(day, this.startDay) && (this.minutes === null || this.minutes < DAY))) {
      // the end sets the Duration (a start-day end keeps one under a day)
      this.minutes = this.daysBetween(this.startDay, day) * DAY;
      this.durationTarget.value = this.words(this.minutes);
    }
    this.reconciled = null;
    this.draw();
  }

  // ---- the rule (as Tasks::DurationDates) ----

  endFor(start, minutes) {
    if (!start) return null;
    if (minutes === null || minutes < DAY) return start;
    const days = Math.ceil(minutes / DAY);
    if (!this.working) return this.add(start, days - 1);
    let counted = 0;
    let day = start;
    for (let i = 0; i < LIMIT; i += 1) {
      if (this.isWorking(day)) counted += 1;
      if (counted >= days) break;
      day = this.add(day, 1);
    }
    return day;
  }

  daysBetween(start, finish) {
    if (finish <= start) return 1;
    if (!this.working) return Math.round((finish - start) / 86400000) + 1;
    let count = 0;
    for (let day = start; day <= finish; day = this.add(day, 1)) if (this.isWorking(day)) count += 1;
    return Math.max(count, 1);
  }

  isWorking(day) {
    return this.works.includes(day.getDay()) && !this.off[this.key(day)];
  }

  // What Task#duration_in_words= reads: a bare number is minutes, a bare
  // decimal needs a unit, otherwise "45 minutes", "1 h 10 min", "3 days"
  readMinutes(text) {
    const typed = (text || '').trim().toLowerCase();
    if (!typed) return null;
    if (/^\d+$/.test(typed)) return Number(typed) > 0 ? Number(typed) : NaN;
    if (/^\d*\.\d+$/.test(typed)) return NaN;
    const units = { w: 10080, d: 1440, h: 60, m: 1 };
    let total = 0;
    let found = false;
    typed.replace(/(\d+(?:\.\d+)?)\s*(w|d|h|m)[a-z]*/g, (_, n, unit) => { total += parseFloat(n) * units[unit]; found = true; return ''; });
    return found && total > 0 ? Math.round(total) : NaN;
  }

  words(minutes) {
    if (minutes === null) return '';
    if (minutes % DAY === 0) return this.plural(minutes / DAY, 'day');
    if (minutes % 60 === 0) return this.plural(minutes / 60, 'hour');
    if (minutes < 60) return this.plural(minutes, 'minute');
    return `${this.plural(Math.floor(minutes / 60), 'hour')} ${this.plural(minutes % 60, 'minute')}`;
  }

  plural(n, unit) {
    return `${n} ${unit}${n === 1 ? '' : 's'}`;
  }

  // ---- drawing ----

  draw() {
    this.wasTarget.textContent = this.minutes === this.stored ? '' : `was ${this.stored === null ? 'none' : this.words(this.stored)}`;
    if (!this.hasCalendarTarget) return;
    const end = this.endFor(this.startDay, this.minutes);
    this.drawFields(end);
    this.drawCalendar(end);
    this.drawNote(end);
  }

  drawFields(end) {
    const start = this.startDay;
    let startNote = '';
    if (start && this.started) startNote = this.since ? `In Process since ${this.since}` : '';
    else if (start && start < this.today && !this.finished) startNote = 'passed';
    this.startFieldTarget.innerHTML = `<span class="dp-field-k">Start</span>
      <span class="dp-field-v">${start ? this.label(start) : 'Pick a day'}</span>
      <span class="dp-field-s${startNote === 'passed' ? ' is-warn' : ''}">${startNote || ''}</span>`;
    let endNote = '';
    if (end) endNote = this.minutes === null ? '1 day: no Duration' : `start + ${this.words(this.minutes)}`;
    const endPast = end && end < this.today && !this.finished;
    this.endFieldTarget.innerHTML = `<span class="dp-field-k">End</span>
      <span class="dp-field-v">${end ? this.label(end) : '–'}</span>
      <span class="dp-field-s${endPast ? ' is-warn' : ''}">${endPast ? 'passed · ' : ''}${this.escape(endNote)}</span>`;
    this.startFieldTarget.classList.toggle('is-on', this.active === 'start');
    this.endFieldTarget.classList.toggle('is-on', this.active === 'end');
    this.promptTarget.innerHTML = this.active === 'start'
      ? `Click the day it <b>${this.finished ? 'started' : 'starts'}</b>; its Duration comes along.`
      : `Click the day it <b>${this.finished ? 'ended' : 'ends'}</b>; the Duration changes to match.`;
  }

  drawCalendar(end) {
    const year = this.month.getFullYear();
    const month = this.month.getMonth();
    const lead = (new Date(year, month, 1).getDay() + 6) % 7; // weeks start on Monday, as the Schedule's do
    const days = new Date(year, month + 1, 0).getDate();
    const thisMonth = year === this.today.getFullYear() && month === this.today.getMonth();
    let html = `<div class="dp-cal-head">
      <button type="button" class="dp-nav focus:outline-none" data-month="-1" aria-label="Previous month"${thisMonth && !this.started && !this.finished ? ' disabled' : ''}>‹</button>
      <b>${MONTHS[month]} ${year}</b>
      <button type="button" class="dp-nav focus:outline-none" data-month="1" aria-label="Next month">›</button>
    </div><div class="dp-grid">`;
    ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].forEach((d) => { html += `<span class="dp-dow">${d}</span>`; });
    for (let i = 0; i < lead; i += 1) html += '<span></span>';
    for (let n = 1; n <= days; n += 1) {
      const day = new Date(year, month, n);
      const key = this.key(day);
      const classes = ['dp-day', 'focus:outline-none'];
      if (day >= this.today && !this.works.includes(day.getDay())) classes.push('is-nw');
      if (this.off[key]) classes.push('is-off');
      if (this.same(day, this.today)) classes.push('is-today');
      if (this.startDay && end && day > this.startDay && day < end) classes.push('is-run');
      if (this.same(day, this.startDay) || this.same(day, end)) classes.push('is-sel');
      let title = '';
      if (this.off[key]) title = this.offText(key);
      else if (!this.works.includes(day.getDay())) title = this.notWorking(day);
      html += `<button type="button" class="${classes.join(' ')}" data-day="${key}"${this.canPick(day) ? '' : ' disabled'}${title ? ` title="${this.escape(title)}"` : ''}>${n}${this.off[key] ? '<small>Off</small>' : ''}</button>`;
    }
    this.calendarTarget.innerHTML = `${html}</div>`;
  }

  canPick(day) {
    if (this.finished) return day <= this.today;
    if (this.started) return this.active !== 'start' || day <= this.today;
    return day >= this.today;
  }

  drawNote(end) {
    const start = this.startDay;
    let text = '';
    let info = false;
    if (this.finished && end && end > this.today) {
      text = "It's finished, so it can't end after today. Pick an earlier start or end.";
    } else if (this.reconciled && !this.finished) {
      text = `Its dates ran ${this.plural(this.reconciled, 'day')} but its Duration was ${this.stored === null ? 'blank' : this.words(this.stored)}, so the window starts from the dates. Save keeps ${this.words(this.minutes)}.`;
      info = true;
    } else if (this.started && end && end < this.today) {
      text = "Its end has passed and it's still in process. Pick a later end (the Duration follows), or finish it.";
    } else if (!this.started && !this.finished && start && start < this.today) {
      text = 'Its start has passed. Pick a new start, or choose No Dates.';
    } else if (start && this.off[this.key(start)]) {
      text = `${this.offText(this.key(start))} that day.`;
    } else if (start && start >= this.today && !this.works.includes(start.getDay())) {
      text = `${this.notWorking(start)}.`;
    }
    this.noteTarget.hidden = !text;
    this.noteTarget.classList.toggle('is-info', info);
    this.noteTarget.textContent = text;
  }

  // ---- helpers ----

  // Today or the next day the people work and aren't off, within two weeks
  nextWorkingDay() {
    for (let i = 0; i < 14; i += 1) {
      const day = this.add(this.today, i);
      if (this.isWorking(day)) return day;
    }
    return this.today;
  }

  // "Philip doesn't work Mondays"; several people: "Not everyone on it ..."
  notWorking(day) {
    const name = DAYS[day.getDay()];
    if (this.count === 0) return `${name}s aren't working days`;
    if (this.who) return `${this.who} doesn't work ${name}s`;
    return `Not everyone on it works ${name}s`;
  }

  offText(key) {
    const names = this.off[key];
    return `${names} ${names.includes(' and ') ? 'are' : 'is'} off`;
  }

  label(day) {
    const year = day.getFullYear() === this.today.getFullYear() ? '' : `, ${day.getFullYear()}`;
    return `${DAYS[day.getDay()].slice(0, 3)}, ${MONTHS[day.getMonth()].slice(0, 3)} ${day.getDate()}${year}`;
  }

  number(value) {
    return value === null || value === undefined || value === '' ? null : Number(value);
  }

  add(day, n) {
    return new Date(day.getFullYear(), day.getMonth(), day.getDate() + n);
  }

  parse(value) {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    return y && m && d ? new Date(y, m - 1, d) : null;
  }

  key(day) {
    return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
  }

  same(a, b) {
    return !!(a && b && this.key(a) === this.key(b));
  }

  escape(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML.replace(/"/g, '&quot;');
  }
}
